/*
 |------------------------------------------------------------------------------
 | Ajax base utility
 |------------------------------------------------------------------------------
 |
 | This utility functions as a wrapped around jQuery's AJAX implementation
 | and allows us to add extra functionality before submitting the data
 | back to any piece of code retrieving it.
 |
 */

/* ========================================================================== *\
    IMPORTS
\* ========================================================================== */

import assign from 'lodash/assign';
import delay from 'lodash/delay';
import partial from 'lodash/partial';

/* == IMPORTS =============================================================== */



/* ========================================================================== *\
    PRIVATE VARIABLES
\* ========================================================================== */

let
	/**
	 * Calls currently still executing
	 * @type {Number}
	 **/
	callAmount = 0,

	applyDelay = true;

const
	/**
	 * Default timeout used for every call
	 * @type {Number}
	 */
	callTimeout = 45000,

	/**
	 * The time, in milliseconds, to delay non-priority calls on page load.
	 * @type {Number}
	 */
	delayDuration = 50;

/* == PRIVATE VARIABLES ===================================================== */



/* ========================================================================== *\
    EVENTS LISTENERS
\* ========================================================================== */

/**
* [onCallCompleted description]
* @param  {[type]} event [description]
* @return {[type]}       [description]
*/
function onCallCompleted(event) {
	callAmount--;

	// If we're still delaying AJAX calls and all the calls have completed…
	if (
		applyDelay &&
		callAmount === 0
	) {
		// we will cancel the delay.
		// We probably have processed all the initial AJAX calls on page load and
		// given priority to the most import calls.
		applyDelay = false;
	}
}

/* == EVENTS LISTENERS ====================================================== */



/* ========================================================================== *\
    PUBLIC API
\* ========================================================================== */

/**
 * Conditional function to check if all calls are done
 */
function allCallsDone() {
	return callAmount === 0;
}

/**
 * Get amount of calls currently executing
 */
function getActiveCallCount() {
	return callAmount;
}

/**
 * Get the promise object of a webservice url call
 *
 * @param {String} type      The type of request
 * @param {String} url       The url of the service to call, if this is null,
 *        the url will be set to the current page url.
 * @param {Object} data      The params that will be passed to the promise
 *        object when called.
 * @param {Bool} hasPriority Flag to indicate the call has priority during page
 *        load. Non priority calls will be delayed during page load.
 * @param {String} contentType The contentType of the body for the request
 *
 * @return {Promise} The promise generated by the AJAX call
 */
function request(type, url, data, hasPriority, contentType) {
	const
		// Create an object with all the params to configure the AJAX call.
		callParameters = assign(
			// Just an empty object to fill
			{},
			{
				complete: onCallCompleted,
				// Our set timeout
				timeout: callTimeout,
				// The type being predefined by calling one of the methods
				// in the public API
				type: type,
				// and the provided URL
				url: url,
				// the content type of the body sent
				contentType: contentType
			},
			// The data param so we can fill it with any needed
			// extra data or params to override certain behaviour
			data
		);

	// Increment the amount of calls being done
	callAmount++;

	// Check if the delay is cancelled or if the call has been marked as
	// high priority…
	if (
		!applyDelay ||
		hasPriority
	) {
		// perform the AJAX call and return the promise.
		return $.ajax(callParameters);
	}

	// Create a deferred which we can return so the caller can be informed of
	// when the AJAX call is done.
	const
		$deferred = $.Deferred();

	// Wait a set amount of time before initiating the AJAX call. This will allow
	// later calls which are marked as high priority to be send out first.
	delay(() => {
		const ajaxCall = $.ajax(callParameters)
			.done($deferred.resolve)
			.fail($deferred.reject);

		// Pass the abort method of the AJAX call on to the deferred, this will
		// allow the caller to still abort the call when needed.
		$deferred.abort = ajaxCall.abort;
	}, delayDuration);

	// We return the deferred object itself instead of .promise(), this is
	// because we will give the deferred the abort method of the AJAX call and it
	// would be inaccessible to the caller if we return .promise() instead of the
	// deferred object itself.
	return $deferred;
}

/* == PUBLIC API ============================================================ */



/* ========================================================================== *\
    EXPORTS
\* ========================================================================== */

const get = partial(request, 'GET');
const post = partial(request, 'POST');
const remove = partial(request, 'DELETE');

export {
	allCallsDone,
	getActiveCallCount,
	get,
	post,
	remove
};


/* == EXPORTS =============================================================== */